import { useEffect, useState } from 'react'
import { apiFetch, storedToken, clearToken, decodeJwt } from '../lib/api'

const DATA_DELETED = [
  'Your name, email address, and profile picture',
  'Your bio, dietary preferences, location, and availability',
  'All meal invitations you sent or received',
  'All chat messages in your conversations',
  'Your ratings and reviews',
  'Your device notification token (FCM)',
  'Your login history',
]

const DATA_RETAINED = [
  'Anonymised visit analytics (no personal identifiers)',
  'Ratings and reviews you gave to other users (attributed to "Deleted User")',
]

export default function DeleteAccount() {
  const [loggedIn, setLoggedIn] = useState(false)
  const [userName, setUserName] = useState('')
  const [step, setStep] = useState('info') // 'info' | 'confirm' | 'done' | 'error'
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    document.title = 'Delete My Account & Data — Co-Eater'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content',
        'Request deletion of your Co-Eater account and all associated personal data.')
    }
    // Check if there is a valid stored token
    const token = storedToken()
    if (token) {
      const payload = decodeJwt(token)
      if (payload && payload.exp * 1000 > Date.now()) {
        setLoggedIn(true)
        if (payload.name) setUserName(payload.name)
        return
      }
    }
    setLoggedIn(false)
  }, [])

  async function handleDelete() {
    setLoading(true)
    setErrorMsg('')
    try {
      const res = await apiFetch('/users/me', { method: 'DELETE' })
      if (res.ok) {
        clearToken()
        localStorage.clear()
        setStep('done')
      } else {
        const body = await res.json().catch(() => ({}))
        setErrorMsg(body.message || 'Something went wrong. Please try again.')
        setStep('error')
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.')
      setStep('error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 px-4 py-10 sm:px-8">
      <div className="max-w-2xl mx-auto">

        {/* Back link */}
        <a href="/" className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:underline mb-6">
          ← Back to Co-Eater
        </a>

        {step === 'done' ? (
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-200 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h1 className="title-font text-2xl font-extrabold text-gray-900 mb-2">Account Deleted</h1>
            <p className="text-gray-600 text-sm leading-relaxed">
              Your account and all associated personal data have been permanently deleted from Co-Eater.
              This action cannot be undone.
            </p>
            <a href="/" className="mt-6 inline-block rounded-2xl bg-orange-500 text-white font-bold px-6 py-3 text-sm hover:bg-orange-600 transition">
              Go to Homepage
            </a>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="mb-6">
              <h1 className="title-font text-3xl font-extrabold text-gray-900 mb-2">
                Delete Account &amp; Data
              </h1>
              <p className="text-gray-600 text-sm leading-relaxed">
                You can request permanent deletion of your Co-Eater account and all associated
                personal data at any time. This action is <strong>irreversible</strong>.
              </p>
            </header>

            {/* What gets deleted */}
            <section className="bg-white rounded-2xl p-6 border border-red-100 shadow-sm mb-6">
              <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="text-red-500">🗑️</span> Data that will be permanently deleted
              </h2>
              <ul className="space-y-1.5">
                {DATA_DELETED.map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-red-400 mt-0.5 shrink-0">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* What is retained */}
            <section className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm mb-8">
              <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span>📋</span> Data retained for legal / product reasons
              </h2>
              <ul className="space-y-1.5">
                {DATA_RETAINED.map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-gray-400 mt-0.5 shrink-0">–</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-3">
                Retained data contains no personally identifiable information and cannot be linked back to you.
              </p>
            </section>

            {/* Logged-in flow */}
            {loggedIn ? (
              <div className="bg-white rounded-2xl p-6 border border-orange-100 shadow-sm">
                {userName && (
                  <p className="text-sm text-gray-700 mb-4">
                    Signed in as <strong>{userName}</strong>.
                  </p>
                )}

                {step === 'error' && (
                  <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {errorMsg}
                  </div>
                )}

                {step === 'info' && (
                  <button
                    onClick={() => setStep('confirm')}
                    className="w-full rounded-2xl bg-red-500 text-white font-bold py-3 text-sm hover:bg-red-600 transition"
                  >
                    Delete My Account &amp; All Data
                  </button>
                )}

                {(step === 'confirm' || step === 'error') && (
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-4">
                      Are you sure? This will permanently delete your account and all your data.
                      <br />
                      <span className="text-red-600 font-bold">This cannot be undone.</span>
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleDelete}
                        disabled={loading}
                        className="flex-1 rounded-2xl bg-red-600 text-white font-bold py-3 text-sm hover:bg-red-700 transition disabled:opacity-60"
                      >
                        {loading ? 'Deleting…' : 'Yes, Delete Everything'}
                      </button>
                      <button
                        onClick={() => setStep('info')}
                        disabled={loading}
                        className="flex-1 rounded-2xl border border-gray-300 text-gray-700 font-semibold py-3 text-sm hover:bg-gray-50 transition disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Not logged-in flow */
              <div className="bg-white rounded-2xl p-6 border border-orange-100 shadow-sm space-y-4">
                <p className="text-sm text-gray-700">
                  You are not currently signed in. To delete your account, you can either:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li>
                    <strong>Sign in first</strong> —{' '}
                    <a href="/" className="text-orange-600 underline hover:text-orange-700">
                      go to Co-Eater
                    </a>
                    , log in, then return to this page.
                  </li>
                  <li>
                    <strong>Email us</strong> — send a deletion request to{' '}
                    <a
                      href="mailto:privacy@coeater.com?subject=Account%20Deletion%20Request&body=Please%20delete%20my%20Co-Eater%20account%20and%20all%20associated%20data.%20My%20registered%20email%20address%20is%3A%20"
                      className="text-orange-600 underline hover:text-orange-700"
                    >
                      privacy@coeater.com
                    </a>{' '}
                    with the subject <em>"Account Deletion Request"</em> and include your registered email address.
                    We will process your request within 30 days.
                  </li>
                </ol>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <footer className="border-t border-gray-200 mt-10 pt-6 pb-6 text-center">
          <nav className="flex flex-wrap gap-4 justify-center text-xs" aria-label="Footer navigation">
            <a href="/about" className="text-gray-600 hover:text-orange-600 font-medium transition">About</a>
            <span className="text-gray-300">|</span>
            <a href="/privacy-policy" className="text-gray-600 hover:text-orange-600 font-medium transition">Privacy Policy</a>
            <span className="text-gray-300">|</span>
            <a href="/terms-of-use" className="text-gray-600 hover:text-orange-600 font-medium transition">Terms of Use</a>
            <span className="text-gray-300">|</span>
            <a href="/delete-account" className="text-red-600 font-semibold">Delete Account</a>
          </nav>
          <p className="text-xs text-gray-400 mt-4">&copy; {new Date().getFullYear()} Co-Eater. All rights reserved.</p>
        </footer>
      </div>
    </div>
  )
}
